import { Component, effect, inject, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { RefParamsService } from '../ref-params/ref-params.service';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DialogObject } from '@/components/dialog/dialog.types';
import { BehaviorSubject } from 'rxjs';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { getApprovalTypeFormObject } from './utils/approval-type-form-object';
import { getApprovalTypeUpdateDrawerObject } from './utils/approval-type-update-drawer-object';
import { ApprovalType, ApprovalTypeDto, RefTypeEnum } from '@/types';
import { ApprovalTypeRepository } from '@/store/approval-type-state/approval-type-state.repository';
import { ApprovalTypeService } from './approval-type.service';
import { getApprovalTypeDeleteObject } from './utils/approval-type-delete-dialog-object';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { getApprovalTypeDataTableStructure } from './utils/approval-type-data-table-object';
import { getApprovalTypeAddDrawerObject } from './utils/approval-type-add-drawer-object';
import { approvalTypeSchema } from '@/validations/approval-type.validation';

@Component({
  selector: 'sm-approval-type',
  imports: [PageHeaderComponent, DataTableBuilderComponent, DrawerComponent, DialogComponent],
  templateUrl: './approval-type.component.html',
  styleUrl: './approval-type.component.scss',
})
export class ApprovalTypeComponent {
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  refParamsService = inject(RefParamsService);
  approvalTypeService = inject(ApprovalTypeService);
  approvalTypeRepository = inject(ApprovalTypeRepository);

  breadcrumb = [
    {
      label: 'settings.approvalType.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.approvalType.breadcrumb.approvalType',
      routerLink: ['/settings', 'approval-type'],
    },
  ];

  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  approvalTypeDrawer?: DrawerObject;

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  approvalTypeDialog?: DialogObject;

  approvalTypeCategoriesOptions: { label: string; value: number }[] = [];

  formObject = getApprovalTypeFormObject({
    store: this.approvalTypeRepository,
    approvalTypeCategoriesOptions: this.approvalTypeCategoriesOptions,
  });

  isDisabled = new BehaviorSubject<boolean>(true);

  rowActions: DataTableRowActions = {
    editAction: (row: ApprovalType) => {
      this.approvalTypeDrawer = getApprovalTypeUpdateDrawerObject({
        store: this.approvalTypeRepository,
        formObject: this.formObject,
        action: () => this.updateApprovalType(row.id, this.approvalTypeRepository.get('dto')),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    deleteAction: (row: ApprovalType) => {
      this.approvalTypeDialog = getApprovalTypeDeleteObject({
        store: this.approvalTypeRepository,
        action: () => {
          this.deleteActivity(row.id);
          this.dialogComponent.toggleVisible();
        },
        toggleVisiblity: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
  };

  loading = true;
  serverQuery = createServerQuery({});
  data: ApprovalType[] = [];
  totalRecords = 0;

  dataTableObject: DynamicDataTable = getApprovalTypeDataTableStructure({
    store: this.approvalTypeRepository,
    rowActions: this.rowActions,
    createAction: () => {
      this.approvalTypeDrawer = getApprovalTypeAddDrawerObject({
        store: this.approvalTypeRepository,
        formObject: this.formObject,
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
        action: () => this.addApprovalType(),
      });
      this.drawerComponent.toggleVisible();
    },
    enableServerActions: true,
    serverQuery: this.serverQuery,
  });

  constructor() {
    effect(() => {
      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const search = this.serverQuery.search();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();

      this.loadData(page, size, search, sortBy, sortOrder);
    });
  }

  loadData(page = 0, size = 10, search = '', sortBy = '', sortOrder = 'asc') {
    const sort = sortBy ? `${sortBy},${sortOrder}` : '';
    this.approvalTypeService.getAll({ page, size, search, sort }).subscribe({
      next: data => {
        this.totalRecords = (data as any).totalElements ?? data.content.length;
        this.data = (data as any).content ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.approvalType.message.error.loading'),
        });
      },
    });
    this.refParamsService.getSpecializedRefParams(RefTypeEnum.APPROVAL_ISSUING_BODY).subscribe({
      next: data => {
        this.approvalTypeCategoriesOptions = data.map(cat => ({ label: cat.description, value: cat.id }));
        this.formObject = getApprovalTypeFormObject({
          store: this.approvalTypeRepository,
          approvalTypeCategoriesOptions: this.approvalTypeCategoriesOptions,
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.approvalType.message.error.loadingApprovalIssuingBody'),
        });
      },
    });
  }

  addApprovalType() {
    const result = approvalTypeSchema.safeParse(this.approvalTypeRepository.get('dto'));
    if (result.success) {
      this.approvalTypeService.create(this.approvalTypeRepository.get('dto')).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(
            this.serverQuery.page(),
            this.serverQuery.pageSize(),
            this.serverQuery.search(),
            this.serverQuery.sortBy(),
            this.serverQuery.sortOrder()
          );
          this.approvalTypeRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.approvalType.message.success.added'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.approvalType.message.error.added'),
          });
        },
      });
    } else {
      this.approvalTypeRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  updateApprovalType(id: number, approvalType: ApprovalTypeDto) {
    const result = approvalTypeSchema.safeParse(approvalType);
    if (result.success) {
      this.approvalTypeService.update(id, approvalType).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(
            this.serverQuery.page(),
            this.serverQuery.pageSize(),
            this.serverQuery.search(),
            this.serverQuery.sortBy(),
            this.serverQuery.sortOrder()
          );
          this.approvalTypeRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.approvalType.message.success.edited'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.approvalType.message.error.edited'),
          });
        },
      });
    } else {
      this.approvalTypeRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  deleteActivity(id: number) {
    this.approvalTypeService.delete(id).subscribe({
      next: () => {
        this.loadData(
          this.serverQuery.page(),
          this.serverQuery.pageSize(),
          this.serverQuery.search(),
          this.serverQuery.sortBy(),
          this.serverQuery.sortOrder()
        );
        this.approvalTypeRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.approvalType.message.success.deleted'),
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.approvalType.message.error.deleted'),
        });
      },
    });
  }
}
